import { useEffect, useState } from "react";
import * as api from "../api";
import { compactAccount, dayKeys, nextWorkday, type Ready } from "./cache";
import { pruneDays, type Scope } from "./db";
import { ROUTE_DAY_GET, shareFetch } from "./share";

/** A role without the page gets 403 or 404; the day is ready without that part. */
const optional = <T>(work: Promise<T>) =>
  work.catch((e) => {
    if (e instanceof api.ApiError && (e.status === 403 || e.status === 404)) return null;
    throw e;
  });

/** Keep each part that Today and Visit need offline; `day` skips the fetch of today's route. */
export async function refreshDay(scope: Scope, token: string, day: api.RouteDay | null): Promise<number> {
  const today = day ?? (await shareFetch(scope.key(ROUTE_DAY_GET), () => api.routeDay(token)));
  const next = nextWorkday(today.today);
  const [loads, nextDay, defaults, accounts, labels] = await Promise.all([
    day ? null : optional(api.loads(token, today.today)),
    api.routeDay(token, next),
    optional(api.visitDefaults(token)),
    optional(api.accounts(token)),
    api.visitLabels(token).catch(() => null),
  ]);
  if (!day) await scope.write(dayKeys.route(today.today), today);
  if (loads) await scope.write(dayKeys.loads(today.today), loads);
  await scope.write(dayKeys.route(next), nextDay);
  if (defaults) await scope.write(dayKeys.defaults, defaults);
  if (accounts) await scope.write(dayKeys.accounts, accounts.map(compactAccount));
  if (labels) await scope.write(dayKeys.labels, labels);
  const at = Date.now();
  const stops = today.routes.filter((route) => route.mine && route.status !== "cancelled").reduce((n, route) => n + route.stops.length, 0);
  await scope.write<Ready>(dayKeys.ready(today.today), { stops }, at);
  await pruneDays(scope, today.today);
  return at;
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    addEventListener("online", on);
    addEventListener("offline", off);
    return () => {
      removeEventListener("online", on);
      removeEventListener("offline", off);
    };
  }, []);
  return online;
}
